import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import DataGrid from '../../../shared/DataGrid/StyledDataGrid';
import styled from 'styled-components';
import { StyledRouterLink } from '../../../shared/StyledLink/StyledLink';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { restaurantPageState } from '../state';
import { useCallback, useMemo, useState } from 'react';
import dayjs from '../../../shared/utils/dayJsCustomParseFormat';
import { ShopOpeningHoursDto, WebshopShopDto } from '@kotipizzagroup/kotipizza-shops-api-admin-client';
import { OutOfStockItem, useRestaurantData } from '../hooks/useRestaurantData';
import { isScheduleInEffect } from '../../../shared/utils/outOfStockFilters';

type Props = {
  restaurants: WebshopShopDto[];
};

type OpeningHoursCheckResponse = {
  exception?: openingHourException;
  notices: string[];
};

type Row = {
  id: string;
  displayName: string;
  slug: string;
  isActive: boolean;
  currentPickupEstimate: number | openingHourException;
  currentDeliveryEstimate: number | openingHourException;
  notices: string[];
  outOfStockItems: OutOfStockItem[];
};

enum openingHourException {
  Closed = 'Closed',
}

const expectedClosingTime = '20:30';
const lastClosingTime = '06:00';
const deliveryOpeningHourId = 2;
const pickupOpeningHourId = 4;

const Info: React.FC<Props> = ({ restaurants }) => {
  const { t } = useTranslation();

  const { getRestaurantOutOfStockProducts, isLoading } = useRestaurantData();

  const checkOpening = useCallback(
    (
      shopOpeningHours: ShopOpeningHoursDto[],
      openingHourId: number,
      methodName: string,
      isMethodOpen: boolean
    ): OpeningHoursCheckResponse => {
      const shopOpeningHour = shopOpeningHours.find(
        (shopOpeningHour) => shopOpeningHour.openingHourTypeId === openingHourId
      );

      if (!isMethodOpen) {
        return {
          exception: openingHourException.Closed,
          notices: [`${methodName} ${t('disabled')}`],
        };
      }

      if (!shopOpeningHour) {
        return {
          exception: openingHourException.Closed,
          notices: [`${t('Failed to retrieve opening hours')} (${methodName})`],
        };
      }

      const shopClosingTime = getEndTime(shopOpeningHour);

      const isResticted =
        isTimeBefore(shopClosingTime, expectedClosingTime) && !isTimeBefore(shopClosingTime, lastClosingTime);

      if (isResticted) {
        return {
          notices: [`${methodName} ${getOpeningHours(shopOpeningHour)}`],
        };
      }

      return {
        notices: [],
      };
    },
    [t]
  );

  const state = restaurantPageState.getState();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: state.tabs.info.page,
  });

  const columns: GridColDef<Row>[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
    },
    {
      field: 'isActive',
      headerName: t('Active'),
      width: 100,
      renderCell(params) {
        return params.row.isActive ? <CheckCircle color="success" /> : <Cancel color="disabled" />;
      },
    },
    {
      field: 'displayName',
      headerName: t('restaurants.list.name'),
      flex: 2,
      minWidth: 300,
      renderCell(params) {
        return <StyledRouterLink to={`/restaurants/${params.row.id}`}>{params.row.displayName}</StyledRouterLink>;
      },
    },
    {
      field: 'slug',
      headerName: t('Slug'),
      flex: 1,
      minWidth: 200,
      renderCell(params) {
        return params.row.slug;
      },
    },
    {
      field: 'currentPickupEstimate',
      headerName: t('Pickup'),
      width: 100,
      renderCell(params) {
        return <Estimate estimateOrException={params.row.currentPickupEstimate} />;
      },
    },
    {
      field: 'currentDeliveryEstimate',
      headerName: t('Delivery'),
      width: 100,
      renderCell(params) {
        return <Estimate estimateOrException={params.row.currentDeliveryEstimate} />;
      },
    },
    {
      field: 'notices',
      headerName: t('Notices'),
      minWidth: 350,
      flex: 2,
      renderCell(params) {
        return (
          <div>
            {params.row.notices.map((notice) => (
              <WarningText key={`${params.row.id}-notice-${notice}`}>{notice}</WarningText>
            ))}
            {params.row.outOfStockItems.map((item) => (
              <OutOfStock key={`${params.row.id}-outofstock-${item.name}-${item.id}`} outOfStockItem={item} />
            ))}
          </div>
        );
      },
    },
  ];

  const rows: Row[] = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return restaurants.map((restaurant) => {
      const notices: string[] = [];
      const restaurantOutOfStockProducts = getRestaurantOutOfStockProducts(restaurant.shopExternalId);

      let pickupException: undefined | openingHourException;
      let deliveryException: undefined | openingHourException;

      if (!restaurant.hasWebSale) {
        pickupException = openingHourException.Closed;
        deliveryException = openingHourException.Closed;
        notices.push(t('websale disabled'));
      } else {
        const pickupOpeningHours = checkOpening(
          restaurant.shopOpeningHours ?? [],
          pickupOpeningHourId,
          'nouto',
          restaurant.hasPickup
        );

        const deliveryOpeningHours = checkOpening(
          restaurant.shopOpeningHours ?? [],
          deliveryOpeningHourId,
          'kuljetus',
          restaurant.hasDelivery
        );

        pickupException = pickupOpeningHours.exception;
        deliveryException = deliveryOpeningHours.exception;

        notices.push(...pickupOpeningHours.notices);
        notices.push(...deliveryOpeningHours.notices);
      }

      const activeOutOfStockItems = restaurantOutOfStockProducts
        .filter((item) => item.isActive === true)
        .filter(isScheduleInEffect);

      return {
        id: restaurant.shopExternalId,
        displayName: restaurant.displayName,
        slug: restaurant.slug || '',
        isActive: restaurant.isActive,
        currentPickupEstimate: pickupException ?? restaurant.currentPickupEstimate,
        currentDeliveryEstimate: deliveryException ?? restaurant.currentDeliveryEstimate,
        notices: notices,
        outOfStockItems: activeOutOfStockItems,
      };
    });
  }, [isLoading, restaurants, getRestaurantOutOfStockProducts, t, checkOpening]);

  const handlePagination = (model: GridPaginationModel) => {
    setPaginationModel(model);

    restaurantPageState.setState({
      tabs: {
        ...state.tabs,
        info: {
          ...state.tabs.info,
          page: model.page,
        },
      },
    });
  };

  return (
    <Box>
      <DataGridContainer>
        <DataGrid
          loading={isLoading}
          columns={columns}
          getRowHeight={(params) => {
            const defaultRowHeight = 52;

            const model = params.model as Row;
            const totalNotices = model.notices.length + model.outOfStockItems.length;

            if (totalNotices > 2) {
              return 'auto';
            }

            return defaultRowHeight;
          }}
          rows={rows}
          initialState={{ sorting: { sortModel: [{ field: 'id', sort: 'asc' }] } }}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePagination}
        />
      </DataGridContainer>
    </Box>
  );
};

export const DataGridContainer = styled(Box)`
  height: calc(100vh - 400px);
`;

const getEndTime = (openingHours: ShopOpeningHoursDto): string => {
  const date = new Date().getDay();

  switch (date) {
    case 0:
      return openingHours.sunEndTime;
    case 1:
      return openingHours.monEndTime;
    case 2:
      return openingHours.tueEndTime;
    case 3:
      return openingHours.wedEndTime;
    case 4:
      return openingHours.thuEndTime;
    case 5:
      return openingHours.friEndTime;
    case 6:
      return openingHours.satEndTime;
    default:
      return '';
  }
};

const getOpeningHours = (openingHours: ShopOpeningHoursDto): string => {
  const date = new Date().getDay();

  switch (date) {
    case 0:
      return `${openingHours.sunStartTime} – ${openingHours.sunEndTime}`;
    case 1:
      return `${openingHours.monStartTime} – ${openingHours.monEndTime}`;
    case 2:
      return `${openingHours.tueStartTime} – ${openingHours.tueEndTime}`;
    case 3:
      return `${openingHours.wedStartTime} – ${openingHours.wedEndTime}`;
    case 4:
      return `${openingHours.thuStartTime} – ${openingHours.thuEndTime}`;
    case 5:
      return `${openingHours.friStartTime} – ${openingHours.friEndTime}`;
    case 6:
      return `${openingHours.satStartTime} – ${openingHours.satEndTime}`;
    default:
      return '';
  }
};

const isTimeBefore = (time: string, expected: string): boolean => {
  const timeFormat = 'HH:mm';
  return dayjs(time, timeFormat).isBefore(dayjs(expected, timeFormat));
};

const Estimate: React.FC<{ estimateOrException: openingHourException | number }> = ({ estimateOrException }) => {
  const { t } = useTranslation();

  if (typeof estimateOrException !== 'number') {
    return <ErrorText>{t('Closed')}</ErrorText>;
  }

  if (estimateOrException >= 60) {
    // eslint-disable-next-line i18next/no-literal-string
    return <WarningText>{estimateOrException} min</WarningText>;
  }

  // eslint-disable-next-line i18next/no-literal-string
  return <span>{estimateOrException} min</span>;
};

const OutOfStock: React.FC<{ outOfStockItem: OutOfStockItem }> = ({ outOfStockItem }) => {
  const { t } = useTranslation();
  return (
    <div>
      <ErrorSpan>{outOfStockItem.name}</ErrorSpan> {t('out of stock')}{' '}
      {outOfStockItem.added > 0 ? `${outOfStockItem.added} ${t('days ago')}` : t('today')}
    </div>
  );
};

const WarningText = styled.div`
  color: ${({ theme }) => theme.palette.warning.main};
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.palette.error.main};
  font-weight: bold;
`;

const ErrorSpan = styled.span`
  color: ${({ theme }) => theme.palette.error.main};
  font-weight: bold;
`;

export default Info;
